"use client";

import type { SessionAgentState } from "@/shared/lesson/view-contracts";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";

type Point = { x: number; y: number };
type ChickyNode = Point & { restX: number; restY: number; vx: number; vy: number };

export interface CrayonChickyProps {
  agentState?: SessionAgentState | string;
  interactive?: boolean;
  className?: string;
  showHint?: boolean;
}

interface PointerState {
  active: boolean;
  hover: boolean;
  id: number | null;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  travel: number;
  ripple: number;
  weights: Float32Array | null;
}

interface Scribble {
  start: Point;
  control: Point;
  end: Point;
}

interface WarpGeometry {
  restCenter: Point;
  currentCenter: Point;
  radius: number;
  scale: number;
}

const VIEWBOX_SIZE = 200;
const BODY_CENTER = { x: 100, y: 93 } as const;
const BODY_RADIUS = 61;
const BODY_NODE_COUNT = 40;

// The explainer artwork treats Chicky as a drawn circle, with the character
// supplied by the glasses, tiny beak, comb, and feet rather than anatomy.
const CHICKY_OUTLINE: readonly Point[] = Array.from({ length: BODY_NODE_COUNT }, (_, index) => {
  const angle = -Math.PI / 2 + (index / BODY_NODE_COUNT) * Math.PI * 2;
  return {
    x: BODY_CENTER.x + Math.cos(angle) * BODY_RADIUS,
    y: BODY_CENTER.y + Math.sin(angle) * BODY_RADIUS,
  };
});

const STATE_MOTION: Record<
  SessionAgentState,
  { amplitude: number; frequency: number; homeStrength: number; lineAlpha: number }
> = {
  connecting: { amplitude: 0.55, frequency: 1.4, homeStrength: 44, lineAlpha: 0.74 },
  thinking: { amplitude: 1.15, frequency: 2.5, homeStrength: 38, lineAlpha: 0.9 },
  speaking: { amplitude: 1.45, frequency: 3.2, homeStrength: 34, lineAlpha: 1 },
  listening: { amplitude: 0.85, frequency: 1.8, homeStrength: 31, lineAlpha: 0.92 },
  idle: { amplitude: 0.5, frequency: 1.25, homeStrength: 32, lineAlpha: 0.84 },
  disconnected: { amplitude: 0, frequency: 0, homeStrength: 52, lineAlpha: 0.48 },
  error: { amplitude: 0, frequency: 0, homeStrength: 58, lineAlpha: 0.42 },
};

function createScribbles(): readonly Scribble[] {
  let seed = 0x43484943;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };

  return Array.from({ length: 52 }, () => {
    const angle = random() * Math.PI * 2;
    const radius = Math.sqrt(random()) * 52;
    const x = BODY_CENTER.x + Math.cos(angle) * radius;
    const y = BODY_CENTER.y + Math.sin(angle) * radius;
    const length = 5 + random() * 14;
    const tilt = -0.32 + random() * 0.64;
    return {
      start: { x: x - length / 2, y: y - tilt * length * 0.25 },
      control: { x, y: y + (random() - 0.5) * 2.4 },
      end: { x: x + length / 2, y: y + tilt * length * 0.25 },
    };
  });
}

const BODY_SCRIBBLES = createScribbles();

function normalizeAgentState(agentState: CrayonChickyProps["agentState"]): SessionAgentState {
  if (agentState && agentState in STATE_MOTION) return agentState as SessionAgentState;
  return "idle";
}

function createNodes(width: number, height: number): ChickyNode[] {
  const scale = (Math.min(width, height) * 0.94) / VIEWBOX_SIZE;
  const offsetX = (width - VIEWBOX_SIZE * scale) / 2;
  const offsetY = (height - VIEWBOX_SIZE * scale) / 2;

  return CHICKY_OUTLINE.map(({ x, y }) => {
    const restX = offsetX + x * scale;
    const restY = offsetY + y * scale;
    return { x: restX, y: restY, restX, restY, vx: 0, vy: 0 };
  });
}

function polygonArea(points: readonly Point[]) {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area) * 0.5;
}

function polygonCenter(points: readonly Point[]) {
  let x = 0;
  let y = 0;
  for (const point of points) {
    x += point.x;
    y += point.y;
  }
  return { x: x / points.length, y: y / points.length };
}

function pointInPolygon(point: Point, polygon: readonly Point[]) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index];
    const previousPoint = polygon[previous];
    const crosses =
      currentPoint.y > point.y !== previousPoint.y > point.y &&
      point.x <
        ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
          (previousPoint.y - currentPoint.y || 1) +
          currentPoint.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function drawSmoothLoop(context: CanvasRenderingContext2D, points: readonly Point[]) {
  const last = points[points.length - 1];
  const first = points[0];
  context.beginPath();
  context.moveTo((last.x + first.x) / 2, (last.y + first.y) / 2);
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    context.quadraticCurveTo(
      current.x,
      current.y,
      (current.x + next.x) / 2,
      (current.y + next.y) / 2
    );
  }
  context.closePath();
}

function createWarpGeometry(nodes: readonly ChickyNode[]): WarpGeometry {
  const restCenter = polygonCenter(nodes.map((node) => ({ x: node.restX, y: node.restY })));
  const radius =
    nodes.reduce(
      (total, node) => total + Math.hypot(node.restX - restCenter.x, node.restY - restCenter.y),
      0
    ) / nodes.length;
  return {
    restCenter,
    currentCenter: polygonCenter(nodes),
    radius,
    scale: radius / BODY_RADIUS,
  };
}

// Bind an authored point to the soft body. Interior details inherit the center
// motion and progressively pick up the local boundary deformation toward the edge.
function warpPoint(point: Point, nodes: readonly ChickyNode[], geometry: WarpGeometry): Point {
  const restPoint = {
    x: geometry.restCenter.x + (point.x - BODY_CENTER.x) * geometry.scale,
    y: geometry.restCenter.y + (point.y - BODY_CENTER.y) * geometry.scale,
  };
  const localX = restPoint.x - geometry.restCenter.x;
  const localY = restPoint.y - geometry.restCenter.y;
  const radialRatio = Math.min(1, Math.hypot(localX, localY) / Math.max(geometry.radius, 1));
  const angle = Math.atan2(localY, localX);
  const normalizedAngle = (angle + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
  const floatIndex = (normalizedAngle / (Math.PI * 2)) * nodes.length;
  const lowerIndex = Math.floor(floatIndex) % nodes.length;
  const upperIndex = (lowerIndex + 1) % nodes.length;
  const mix = floatIndex - Math.floor(floatIndex);
  const lower = nodes[lowerIndex];
  const upper = nodes[upperIndex];
  const boundaryShift = {
    x: (lower.x - lower.restX) * (1 - mix) + (upper.x - upper.restX) * mix,
    y: (lower.y - lower.restY) * (1 - mix) + (upper.y - upper.restY) * mix,
  };
  const centerShift = {
    x: geometry.currentCenter.x - geometry.restCenter.x,
    y: geometry.currentCenter.y - geometry.restCenter.y,
  };
  const influence = Math.pow(radialRatio, 0.72);
  return {
    x: restPoint.x + centerShift.x * (1 - influence) + boundaryShift.x * influence,
    y: restPoint.y + centerShift.y * (1 - influence) + boundaryShift.y * influence,
  };
}

function drawWarpedLoop(
  context: CanvasRenderingContext2D,
  nodes: readonly ChickyNode[],
  geometry: WarpGeometry,
  points: readonly Point[]
) {
  drawSmoothLoop(
    context,
    points.map((point) => warpPoint(point, nodes, geometry))
  );
}

function circlePoints(center: Point, radius: number, count = 20) {
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    return { x: center.x + Math.cos(angle) * radius, y: center.y + Math.sin(angle) * radius };
  });
}

function drawLineThroughPoints(
  context: CanvasRenderingContext2D,
  nodes: readonly ChickyNode[],
  geometry: WarpGeometry,
  points: readonly Point[]
) {
  const [first, ...rest] = points.map((point) => warpPoint(point, nodes, geometry));
  context.beginPath();
  context.moveTo(first.x, first.y);
  for (const point of rest) context.lineTo(point.x, point.y);
  context.stroke();
}

function drawChicky(
  context: CanvasRenderingContext2D,
  nodes: readonly ChickyNode[],
  state: SessionAgentState,
  pointer: PointerState
) {
  const canvas = context.canvas;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  const center = polygonCenter(nodes);
  const geometry = createWarpGeometry(nodes);
  const motion = STATE_MOTION[state];
  const graphite = `rgba(54,45,38,${0.56 + motion.lineAlpha * 0.34})`;

  context.clearRect(0, 0, width, height);
  context.save();
  context.lineCap = "round";
  context.lineJoin = "round";

  const glow = context.createRadialGradient(
    center.x,
    center.y,
    3,
    center.x,
    center.y,
    width * 0.47
  );
  glow.addColorStop(0, "rgba(250,204,21,0.19)");
  glow.addColorStop(0.68, "rgba(249,115,22,0.06)");
  glow.addColorStop(1, "rgba(249,115,22,0)");
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  const comb = [
    { x: 83, y: 36 },
    { x: 81, y: 29 },
    { x: 84, y: 23 },
    { x: 90, y: 22 },
    { x: 95, y: 28 },
    { x: 96, y: 20 },
    { x: 102, y: 17 },
    { x: 108, y: 20 },
    { x: 109, y: 28 },
    { x: 115, y: 23 },
    { x: 122, y: 25 },
    { x: 124, y: 32 },
    { x: 119, y: 38 },
  ];
  drawWarpedLoop(context, nodes, geometry, comb);
  context.fillStyle = "rgba(249,115,22,0.96)";
  context.fill();
  context.strokeStyle = graphite;
  context.lineWidth = 2;
  context.stroke();

  context.strokeStyle = "rgba(249,115,22,0.96)";
  context.lineWidth = 4.2;
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 87, y: 146 },
    { x: 87, y: 160 },
    { x: 80, y: 164 },
    { x: 87, y: 160 },
    { x: 94, y: 164 },
  ]);
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 113, y: 146 },
    { x: 113, y: 160 },
    { x: 106, y: 164 },
    { x: 113, y: 160 },
    { x: 120, y: 164 },
  ]);
  context.strokeStyle = "rgba(124,45,18,0.56)";
  context.lineWidth = 1.1;
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 87, y: 147 },
    { x: 87, y: 160 },
    { x: 80, y: 164 },
  ]);
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 113, y: 147 },
    { x: 113, y: 160 },
    { x: 120, y: 164 },
  ]);

  drawSmoothLoop(context, nodes);
  const bodyFill = context.createLinearGradient(0, height * 0.18, 0, height * 0.78);
  bodyFill.addColorStop(0, state === "error" ? "rgba(251,191,36,0.9)" : "rgba(253,224,71,0.98)");
  bodyFill.addColorStop(0.7, "rgba(250,204,21,0.96)");
  bodyFill.addColorStop(1, "rgba(245,158,11,0.91)");
  context.fillStyle = bodyFill;
  context.fill();

  context.save();
  drawSmoothLoop(context, nodes);
  context.clip();
  context.strokeStyle = `rgba(180,83,9,${0.07 + motion.lineAlpha * 0.055})`;
  context.lineWidth = 1.15;
  for (const scribble of BODY_SCRIBBLES) {
    const start = warpPoint(scribble.start, nodes, geometry);
    const control = warpPoint(scribble.control, nodes, geometry);
    const end = warpPoint(scribble.end, nodes, geometry);
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.quadraticCurveTo(control.x, control.y, end.x, end.y);
    context.stroke();
  }
  context.restore();

  drawSmoothLoop(context, nodes);
  context.strokeStyle = graphite;
  context.lineWidth = 2.25;
  context.shadowColor = "rgba(249,115,22,0.2)";
  context.shadowBlur = 7;
  context.stroke();
  context.shadowBlur = 0;
  drawSmoothLoop(
    context,
    nodes.map((node, index) => ({
      x: node.x + Math.sin(index * 2.17) * 0.45,
      y: node.y + Math.cos(index * 1.73) * 0.4,
    }))
  );
  context.strokeStyle = `rgba(120,74,38,${0.12 + motion.lineAlpha * 0.11})`;
  context.lineWidth = 0.9;
  context.stroke();

  context.strokeStyle = graphite;
  context.lineWidth = 1.8;
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 65, y: 83 },
    { x: 48, y: 76 },
    { x: 42, y: 72 },
  ]);
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 135, y: 83 },
    { x: 152, y: 76 },
    { x: 158, y: 72 },
  ]);
  drawLineThroughPoints(context, nodes, geometry, [
    { x: 91, y: 83 },
    { x: 96, y: 85 },
    { x: 100, y: 84 },
    { x: 104, y: 85 },
    { x: 109, y: 83 },
  ]);
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 78, y: 83 }, 13));
  context.stroke();
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 122, y: 83 }, 13));
  context.stroke();

  context.fillStyle = `rgba(38,32,28,${0.74 + motion.lineAlpha * 0.22})`;
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 78, y: 83 }, 3.6, 14));
  context.fill();
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 122, y: 83 }, 3.6, 14));
  context.fill();
  context.fillStyle = "rgba(255,251,235,0.88)";
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 79.2, y: 81.8 }, 0.9, 10));
  context.fill();
  drawWarpedLoop(context, nodes, geometry, circlePoints({ x: 123.2, y: 81.8 }, 0.9, 10));
  context.fill();

  const beak = [
    { x: 100, y: 98 },
    { x: 108, y: 103 },
    { x: 100, y: 113 },
    { x: 92, y: 103 },
  ];
  drawWarpedLoop(context, nodes, geometry, beak);
  context.fillStyle = "rgba(249,115,22,0.98)";
  context.fill();
  context.strokeStyle = "rgba(124,45,18,0.68)";
  context.lineWidth = 1.3;
  context.stroke();

  if (pointer.hover || pointer.ripple > 0.02) {
    context.beginPath();
    context.arc(pointer.x, pointer.y, 5 + pointer.ripple * 12, 0, Math.PI * 2);
    context.strokeStyle = `rgba(255,251,235,${Math.min(0.42, 0.08 + pointer.ripple * 0.34)})`;
    context.lineWidth = 1;
    context.stroke();
  }

  context.restore();
}

function createGrabWeights(nodes: readonly ChickyNode[], point: Point) {
  const center = polygonCenter(nodes);
  const radius =
    nodes.reduce((total, node) => total + Math.hypot(node.x - center.x, node.y - center.y), 0) /
    nodes.length;
  const grabRadius = Math.hypot(point.x - center.x, point.y - center.y) / Math.max(radius, 1);
  const globalWeight = Math.max(0.34, Math.min(0.72, 0.72 - grabRadius * 0.38));
  const spread = radius * 0.86;
  return Float32Array.from(nodes, (node) => {
    const distance = Math.hypot(node.x - point.x, node.y - point.y);
    const localWeight = Math.exp(-(distance * distance) / (2 * spread * spread));
    return Math.min(1, globalWeight + (1 - globalWeight) * localWeight);
  });
}

function deformNodes(nodes: ChickyNode[], weights: Float32Array, deltaX: number, deltaY: number) {
  const safeDeltaX = Math.max(-20, Math.min(20, deltaX));
  const safeDeltaY = Math.max(-20, Math.min(20, deltaY));
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const weight = weights[index];
    node.x += safeDeltaX * weight;
    node.y += safeDeltaY * weight;
    node.vx += safeDeltaX * weight * 18;
    node.vy += safeDeltaY * weight * 18;
  }
}

function pokeNodes(nodes: ChickyNode[], point: Point, weights: Float32Array | null) {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const deltaX = node.x - point.x;
    const deltaY = node.y - point.y;
    const distance = Math.hypot(deltaX, deltaY) || 1;
    const weight = weights?.[index] ?? 0.5;
    node.vx += (deltaX / distance) * 42 * weight;
    node.vy += (deltaY / distance) * 42 * weight;
  }
}

function addSpring(
  nodes: readonly ChickyNode[],
  forces: Float32Array,
  firstIndex: number,
  secondIndex: number,
  strength: number
) {
  const first = nodes[firstIndex];
  const second = nodes[secondIndex];
  const deltaX = second.x - first.x;
  const deltaY = second.y - first.y;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const restDistance = Math.hypot(second.restX - first.restX, second.restY - first.restY);
  const force = (distance - restDistance) * strength;
  const forceX = (deltaX / distance) * force;
  const forceY = (deltaY / distance) * force;
  forces[firstIndex * 2] += forceX;
  forces[firstIndex * 2 + 1] += forceY;
  forces[secondIndex * 2] -= forceX;
  forces[secondIndex * 2 + 1] -= forceY;
}

function stepNodes(
  nodes: ChickyNode[],
  forces: Float32Array,
  deltaSeconds: number,
  elapsedSeconds: number,
  state: SessionAgentState,
  dragging: boolean
) {
  const motion = STATE_MOTION[state];
  const center = polygonCenter(nodes);
  const restPoints = nodes.map((node) => ({ x: node.restX, y: node.restY }));
  const restCenter = polygonCenter(restPoints);
  const restArea = polygonArea(restPoints);
  const currentArea = polygonArea(nodes);
  const areaError = Math.max(
    -0.55,
    Math.min(0.55, (restArea - currentArea) / Math.max(restArea, 1))
  );
  const homeStrength = motion.homeStrength * (dragging ? 0.13 : 1);
  forces.fill(0);

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const radialX = node.restX - restCenter.x;
    const radialY = node.restY - restCenter.y;
    const radialLength = Math.hypot(radialX, radialY) || 1;
    const phase = elapsedSeconds * motion.frequency + index * 0.11;
    const breath = Math.sin(phase) * motion.amplitude;
    const thinkingRipple =
      state === "thinking" ? Math.sin(elapsedSeconds * 4.6 - index * 0.34) * 0.42 : 0;
    const targetX = node.restX + (radialX / radialLength) * (breath + thinkingRipple);
    const targetY = node.restY + (radialY / radialLength) * (breath + thinkingRipple);
    forces[index * 2] += (targetX - node.x) * homeStrength;
    forces[index * 2 + 1] += (targetY - node.y) * homeStrength;

    const currentRadialX = node.x - center.x;
    const currentRadialY = node.y - center.y;
    const currentRadialLength = Math.hypot(currentRadialX, currentRadialY) || 1;
    const areaStrength = dragging ? 82 : 138;
    forces[index * 2] += (currentRadialX / currentRadialLength) * areaError * areaStrength;
    forces[index * 2 + 1] += (currentRadialY / currentRadialLength) * areaError * areaStrength;

    addSpring(nodes, forces, index, (index + 1) % nodes.length, 55);
    addSpring(nodes, forces, index, (index + 2) % nodes.length, 14);
  }

  const damping = Math.exp((dragging ? -4.2 : -6.4) * deltaSeconds);
  for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex += 1) {
    const node = nodes[nodeIndex];
    node.vx = (node.vx + forces[nodeIndex * 2] * deltaSeconds) * damping;
    node.vy = (node.vy + forces[nodeIndex * 2 + 1] * deltaSeconds) * damping;
    node.x += node.vx * deltaSeconds;
    node.y += node.vy * deltaSeconds;

    const offsetX = node.x - node.restX;
    const offsetY = node.y - node.restY;
    const offsetLength = Math.hypot(offsetX, offsetY);
    if (offsetLength > 52) {
      node.x = node.restX + (offsetX / offsetLength) * 52;
      node.y = node.restY + (offsetY / offsetLength) * 52;
      node.vx *= 0.38;
      node.vy *= 0.38;
    }
  }
}

export function CrayonChicky({
  agentState = "idle",
  interactive = true,
  className = "",
  showHint = true,
}: CrayonChickyProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<ChickyNode[]>([]);
  const stateRef = useRef(normalizeAgentState(agentState));
  const [reducedMotion, setReducedMotion] = useState(false);
  const pointerRef = useRef<PointerState>({
    active: false,
    hover: false,
    id: null,
    x: 0,
    y: 0,
    previousX: 0,
    previousY: 0,
    travel: 0,
    ripple: 0,
    weights: null,
  });

  stateRef.current = normalizeAgentState(agentState);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);
    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const forces = new Float32Array(CHICKY_OUTLINE.length * 2);
    let animationFrame = 0;
    let previousTime = performance.now();
    let visible = true;
    let disposed = false;

    const scheduleFrame = () => {
      if (disposed || reducedMotion || !visible || animationFrame !== 0) return;
      animationFrame = window.requestAnimationFrame(drawFrame);
    };

    const resize = () => {
      const width = Math.max(1, canvas.clientWidth);
      const height = Math.max(1, canvas.clientHeight);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodesRef.current = createNodes(width, height);
      drawChicky(context, nodesRef.current, stateRef.current, pointerRef.current);
    };

    const drawFrame = (now: number) => {
      animationFrame = 0;
      if (disposed) return;
      const pointer = pointerRef.current;
      const deltaSeconds = Math.min((now - previousTime) / 1000, 0.033);
      previousTime = now;
      pointer.ripple *= Math.exp(-3.4 * deltaSeconds);

      stepNodes(
        nodesRef.current,
        forces,
        deltaSeconds,
        now / 1000,
        stateRef.current,
        pointer.active
      );
      drawChicky(context, nodesRef.current, stateRef.current, pointer);
      scheduleFrame();
    };

    resize();
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    resizeObserver?.observe(canvas);
    const intersectionObserver =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            visible = entry?.isIntersecting ?? true;
            previousTime = performance.now();
            if (visible) {
              scheduleFrame();
            } else if (animationFrame !== 0) {
              window.cancelAnimationFrame(animationFrame);
              animationFrame = 0;
            }
          });
    intersectionObserver?.observe(canvas);

    if (reducedMotion) {
      drawChicky(context, nodesRef.current, stateRef.current, pointerRef.current);
    } else {
      scheduleFrame();
    }

    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!reducedMotion) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!context || nodesRef.current.length === 0) return;
    drawChicky(context, nodesRef.current, stateRef.current, pointerRef.current);
  }, [agentState, reducedMotion]);

  const getCanvasPoint = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x:
        (event.clientX - bounds.left) *
        (event.currentTarget.clientWidth / Math.max(bounds.width, 1)),
      y:
        (event.clientY - bounds.top) *
        (event.currentTarget.clientHeight / Math.max(bounds.height, 1)),
    };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!interactive || reducedMotion || nodesRef.current.length === 0) return;
    const point = getCanvasPoint(event);
    if (!pointInPolygon(point, nodesRef.current)) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerRef.current = {
      active: true,
      hover: true,
      id: event.pointerId,
      x: point.x,
      y: point.y,
      previousX: point.x,
      previousY: point.y,
      travel: 0,
      ripple: 0.18,
      weights: createGrabWeights(nodesRef.current, point),
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!interactive || reducedMotion) return;
    const point = getCanvasPoint(event);
    const pointer = pointerRef.current;
    pointer.hover = pointInPolygon(point, nodesRef.current);
    pointer.x = point.x;
    pointer.y = point.y;

    if (!pointer.active || pointer.id !== event.pointerId || !pointer.weights) return;
    event.preventDefault();
    const deltaX = point.x - pointer.previousX;
    const deltaY = point.y - pointer.previousY;
    pointer.travel += Math.hypot(deltaX, deltaY);
    pointer.ripple = Math.min(1, pointer.ripple + Math.hypot(deltaX, deltaY) * 0.028);
    deformNodes(nodesRef.current, pointer.weights, deltaX, deltaY);
    pointer.previousX = point.x;
    pointer.previousY = point.y;
  };

  const finishPointer = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const pointer = pointerRef.current;
    if (pointer.id !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (pointer.travel < 5)
      pokeNodes(nodesRef.current, { x: pointer.x, y: pointer.y }, pointer.weights);
    pointer.active = false;
    pointer.id = null;
    pointer.weights = null;
    pointer.ripple = pointer.travel < 5 ? 1 : Math.max(pointer.ripple, 0.35);
  };

  return (
    <div
      className={`relative flex select-none flex-col items-center ${className}`}
      data-testid="interactive-chicky"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className={`h-40 w-40 ${interactive && !reducedMotion ? "touch-none cursor-grab active:cursor-grabbing" : "touch-pan-y"}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onPointerLeave={() => {
          if (!pointerRef.current.active) pointerRef.current.hover = false;
        }}
      />
      {showHint && interactive && !reducedMotion && (
        <span className="-mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-200/55">
          Pull, squish, or tap Chicky
        </span>
      )}
    </div>
  );
}
